import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ReportingIcon from '@mui/icons-material/BarChart';
import AIAssistantIcon from '@mui/icons-material/AutoAwesome';
import { useUserContext } from '../../hooks/useUserContext';
import tenantService from '../../services/tenantService';
import { config } from '../../config';

const DEFAULT_TENANT_ID = config.defaultTenantId;

export interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  showForAdmin?: boolean;
  showForTenantAdmin?: boolean;
}

interface VerticalSidebarProps {
  /**
   * Width of the sidebar when expanded in pixels
   * @default 200
   */
  width?: number;
  
  /**
   * Width of the sidebar when collapsed in pixels
   * @default 64
   */
  collapsedWidth?: number;
  
  /**
   * Initial collapsed state
   * @default false
   */
  defaultCollapsed?: boolean;
  
  /**
   * Custom navigation items (optional)
   */
  items?: NavigationItem[];
}

/**
 * Vertical Sidebar Navigation Component
 *
 * Implements the vertical tabs navigation from Figma Ascend Platform design.
 * Features:
 * - Collapsible sidebar with hamburger menu toggle
 * - 200px default width (expanded) / 64px (collapsed)
 * - Horizontal layout (icon + label side-by-side) when expanded
 * - Icon-only with centered layout when collapsed
 * - Active state with blue fill background + 3px left border accent
 * - Role-based navigation (admin, tenant admin)
 * - Smooth transitions between expanded/collapsed states
 *
 * @component
 */
export default function VerticalSidebar({
  width = 200,
  collapsedWidth = 64,
  defaultCollapsed = false,
  items 
}: VerticalSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isTenantAdmin, backendProfile } = useUserContext();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [tenantName, setTenantName] = useState<string | null>(null);

  // Check if user is on default tenant (individual user)
  const isOnDefaultTenant = !backendProfile?.tenantId || backendProfile.tenantId === DEFAULT_TENANT_ID;

  useEffect(() => {
    if (!backendProfile?.tenantId || isOnDefaultTenant) return;
    tenantService.getTenantById(backendProfile.tenantId).then(tenant => {
      if (tenant?.name) setTenantName(tenant.name);
    }).catch(() => {});
  }, [backendProfile?.tenantId, isOnDefaultTenant]);

  // Default navigation items
  const defaultItems: NavigationItem[] = [
    {
      label: 'Assistant',
      path: '/app/assistant',
      icon: <AIAssistantIcon />,
    },
    {
      label: 'Learning',
      path: '/app/courses',
      icon: <SchoolIcon />,
    },
    {
      label: 'Reporting',
      path: '/app/cpe-history',
      icon: <ReportingIcon />,
    }
  ];

  // Admin navigation items
  const adminItems: NavigationItem[] = [
    {
      label: 'Tenant Admin',
      path: '/app/tenant-admin',
      icon: <BusinessIcon />,
      showForTenantAdmin: true,
    },
    {
      label: 'System Admin',
      path: '/app/system-admin',
      icon: <AdminIcon />,
      showForAdmin: true,
    },
  ];

  const navigationItems = items || [...defaultItems, ...adminItems];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Filter items based on user role
  const filteredItems = navigationItems.filter(item => {
    if (item.showForAdmin && !isAdmin) return false;
    if (item.showForTenantAdmin && !isTenantAdmin && !isAdmin) return false;
    return true;
  });

  return (
    <Box
      sx={{
        width: isCollapsed ? collapsedWidth : width,
        height: '100%',
        // Inherits AppBar's white surface — they read as one continuous app shell,
        // not two near-white panels with a seam between them.
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: { xs: 'none', sm: 'flex' },
        flexDirection: 'column',
        transition: 'width 0.3s ease-in-out',
        flexShrink: 0,
      }}
    >
      {/* Hamburger Menu Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
          px: isCollapsed ? 0 : 1,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tooltip title={isCollapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <List component="nav" sx={{ py: 1, px: 1 }}>
        {filteredItems.map((item, index) => {
          const active = isActive(item.path);

          return (
            <React.Fragment key={item.path}>
              {/* Add divider before admin sections */}
              {(item.showForAdmin || item.showForTenantAdmin) && index > 0 && (
                <Divider sx={{ my: 1, mx: 1 }} />
              )}

              <Tooltip
                title={isCollapsed ? item.label : ''}
                placement="right"
                arrow
              >
                <ListItemButton
                  selected={active}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    position: 'relative',
                    px: isCollapsed ? 1 : 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: isCollapsed ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    gap: isCollapsed ? 0 : 1.25,
                    minHeight: 44,
                    mb: 0.5,
                    // Active: filled bg + left accent bar
                    '&.Mui-selected': {
                      bgcolor: 'rgba(86, 151, 55, 0.10)',
                      '&:hover': {
                        bgcolor: 'rgba(86, 151, 55, 0.14)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 3,
                        borderRadius: '0 2px 2px 0',
                        bgcolor: 'primary.main',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 'unset',
                      color: active ? 'primary.main' : 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': {
                        fontSize: 22,
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.875rem',
                          fontWeight: active ? 600 : 500,
                          lineHeight: 1.4,
                          letterSpacing: '0.01em',
                          color: active ? 'primary.main' : 'text.secondary',
                        },
                      }}
                      sx={{ m: 0 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </List>

      {tenantName && !isOnDefaultTenant && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Tooltip title={isCollapsed ? tenantName : ''} placement="right" arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: 1,
                px: isCollapsed ? 0 : 1.5,
                py: 1.25,
                mx: 1,
                mb: 1,
                mt: 0.5,
                borderRadius: 2,
                bgcolor: 'rgba(86, 151, 55, 0.07)',
                overflow: 'hidden',
                cursor: 'default',
                minHeight: 40,
              }}
            >
              <BusinessIcon
                sx={{
                  fontSize: 18,
                  color: 'primary.main',
                  opacity: 0.8,
                  flexShrink: 0,
                }}
              />
              {!isCollapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    opacity: 0.85,
                    lineHeight: 1.3,
                    letterSpacing: '0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tenantName}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
}
